import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkSeedIconsResponse,
  BulkUpdateIconTagsApplicationErrors,
  BulkUpdateIconTagsByFilterApplicationErrors,
  BulkUpdateIconTagsByFilterOptions,
  BulkUpdateIconTagsByFilterResponse,
  BulkUpdateIconTagsOptions,
  BulkUpdateIconTagsResponse,
  CreateIconOptions,
  Icon,
  SearchIconsOptions,
  SearchIconsResponse,
  SeedItem,
  UpdateIconMetadataIcon,
  bulkSeedIcons as universalBulkSeedIcons,
  bulkUpdateIconTags as universalBulkUpdateIconTags,
  bulkUpdateIconTagsByFilter as universalBulkUpdateIconTagsByFilter,
  createIcon as universalCreateIcon,
  deleteIcon as universalDeleteIcon,
  getIcon as universalGetIcon,
  searchIcons as universalSearchIcons,
  updateIconMetadata as universalUpdateIconMetadata,
} from './enterprise-web-five-v1-icon-icons.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/media-manager' };

/** @internal */
export function createIcon(httpClient: HttpClient): CreateIconSignature {
  return (
    icon: Icon,
    options?: NonNullablePaths<CreateIconOptions, `content` | `mimeType`, 2>
  ) =>
    universalCreateIcon(
      icon,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateIconSignature {
  /**
   * Creates an Icon. On PUBLIC calls the icon's `msid` is set from the caller's
   * signed context; the request body's msid (if any) is ignored. On INTERNAL calls
   * the request may pass `private_msid` to scope the icon to a metasite, otherwise
   * the icon is created as global.
   * @param - Icon metadata to create. Read-only fields on `Icon` (id, revision, created_date,
   * updated_date, msid, media_file_id, media_url) are ignored.
   * @returns The created Icon.
   */
  (
    icon: Icon,
    options?: NonNullablePaths<CreateIconOptions, `content` | `mimeType`, 2>
  ): Promise<
    NonNullablePaths<
      Icon,
      | `name`
      | `keywords`
      | `collection`
      | `category`
      | `type`
      | `mediaFileId`
      | `mediaUrl`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

/** @internal */
export function getIcon(httpClient: HttpClient): GetIconSignature {
  return (iconId: string) =>
    universalGetIcon(
      iconId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetIconSignature {
  /**
   * Retrieves an Icon by ID. Enforces tenancy: returns the icon only if it is
   * global or owned by the caller's metasite. Returns NOT_FOUND otherwise.
   * @param - ID of the Icon to retrieve.
   * @returns The requested Icon.
   */
  (iconId: string): Promise<
    NonNullablePaths<
      Icon,
      | `name`
      | `keywords`
      | `collection`
      | `category`
      | `type`
      | `mediaFileId`
      | `mediaUrl`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

/** @internal */
export function updateIconMetadata(
  httpClient: HttpClient
): UpdateIconMetadataSignature {
  return (
    _id: string,
    icon: NonNullablePaths<UpdateIconMetadataIcon, `revision`, 2>
  ) =>
    universalUpdateIconMetadata(
      _id,
      icon,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateIconMetadataSignature {
  /**
   * Updates metadata fields of an Icon (name, keywords, collection, category, type,
   * weight, description). The binary is immutable post-create; to replace bytes,
   * delete + create. Enforces tenancy on writes (PUBLIC: only on caller-owned
   * private icons; INTERNAL: any).
   * @param - Icon ID.
   * @returns Updated Icon.
   */
  (
    _id: string,
    icon: NonNullablePaths<UpdateIconMetadataIcon, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      Icon,
      | `name`
      | `keywords`
      | `collection`
      | `category`
      | `type`
      | `mediaFileId`
      | `mediaUrl`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

/** @internal */
export function deleteIcon(httpClient: HttpClient): DeleteIconSignature {
  return (iconId: string) =>
    universalDeleteIcon(
      iconId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteIconSignature {
  /**
   * Deletes an Icon by ID. Removes the Vespa document immediately; the Wix Media
   * binary is left in place and cleaned up by a periodic orphan-collector job.
   * Tenancy enforced (PUBLIC cannot delete globals or other clients' private icons).
   * @param - ID of the Icon to delete.
   */
  (iconId: string): Promise<void>;
}

/** @internal */
export function searchIcons(httpClient: HttpClient): SearchIconsSignature {
  return (options?: SearchIconsOptions) =>
    universalSearchIcons(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchIconsSignature {
  /**
   * Hybrid (BM25 + vector) search over icons. Always applies a tenancy filter:
   * returns global icons and the caller's own private icons by default, narrowable
   * via `filter.tenant_scope`. `filter.exclude_ids` drops specific icons from
   * results (useful for clients who don't want certain global icons to appear).
   */
  (options?: SearchIconsOptions): Promise<
    NonNullablePaths<
      SearchIconsResponse,
      | `icons`
      | `icons.${number}.icon.name`
      | `icons.${number}.icon.collection`
      | `icons.${number}.icon.category`
      | `icons.${number}.icon.type`
      | `icons.${number}.icon.mediaFileId`
      | `icons.${number}.icon.mediaUrl`
      | `icons.${number}.relevance`,
      5
    >
  >;
}

/** @internal */
export function bulkUpdateIconTags(
  httpClient: HttpClient
): BulkUpdateIconTagsSignature {
  return (iconIds: string[], options?: BulkUpdateIconTagsOptions) =>
    universalBulkUpdateIconTags(
      iconIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateIconTagsSignature {
  /**
   * Bulk-assign / unassign platform Tags on a list of icons (AIP-7025).
   * A tag that appears in both `assign_tags` and `unassign_tags` is assigned.
   * @param - Icons to update.
   */
  (iconIds: string[], options?: BulkUpdateIconTagsOptions): Promise<
    NonNullablePaths<
      BulkUpdateIconTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateIconTagsApplicationErrors;
    }
  >;
}

/** @internal */
export function bulkUpdateIconTagsByFilter(
  httpClient: HttpClient
): BulkUpdateIconTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateIconTagsByFilterOptions
  ) =>
    universalBulkUpdateIconTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateIconTagsByFilterSignature {
  /**
   * Bulk-assign / unassign platform Tags on icons matching a filter. Async — returns
   * a job id. An empty filter targets all icons.
   * @param - Filter selecting which icons to retag. Empty = all icons.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateIconTagsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkUpdateIconTagsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdateIconTagsByFilterApplicationErrors;
    }
  >;
}

/** @internal */
export function bulkSeedIcons(httpClient: HttpClient): BulkSeedIconsSignature {
  return (items: SeedItem[]) =>
    universalBulkSeedIcons(
      items,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkSeedIconsSignature {
  /**
   * INTERNAL one-off bulk seed for the initial 10k global catalog. Each item carries
   * either inline bytes or an external URL (which the service imports via Wix Media's
   * files/import). Capped at 100 items per request to stay under proto size limits;
   * callers paginate.
   * @param - Items to seed. Each item must carry either inline content or a source URL.
   */
  (items: SeedItem[]): Promise<
    NonNullablePaths<
      BulkSeedIconsResponse,
      | `results`
      | `results.${number}.icon.name`
      | `results.${number}.icon.collection`
      | `results.${number}.icon.category`
      | `results.${number}.icon.type`
      | `results.${number}.icon.mediaFileId`
      | `results.${number}.icon.mediaUrl`
      | `results.${number}.error`,
      5
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  ApplicationError,
  BulkActionMetadata,
  BulkSeedIconsRequest,
  BulkSeedIconsResponse,
  BulkUpdateIconTagsByFilterOptions,
  BulkUpdateIconTagsByFilterRequest,
  BulkUpdateIconTagsByFilterResponse,
  BulkUpdateIconTagsOptions,
  BulkUpdateIconTagsRequest,
  BulkUpdateIconTagsResponse,
  BulkUpdateIconTagsResult,
  CreateIconOptions,
  CreateIconRequest,
  CreateIconResponse,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  DeleteIconRequest,
  DeleteIconResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  GetIconRequest,
  GetIconResponse,
  Icon,
  IconFilter,
  IconTagsModified,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MessageEnvelope,
  RestoreInfo,
  SearchIconsOptions,
  SearchIconsRequest,
  SearchIconsResponse,
  SearchedIcon,
  SeedItem,
  SeedItemSourceOneOf,
  SeedResult,
  TagList,
  Tags,
  TenantScope,
  UpdateIconMetadataIcon,
  UpdateIconMetadataRequest,
  UpdateIconMetadataResponse,
  WebhookIdentityType,
} from './enterprise-web-five-v1-icon-icons.universal.js';
